import { differenceInMonths, parseISO } from 'date-fns';

import { fetchPost, fetchPrivatePost, fetchRelatedPosts, fetchPrivateRelatedPosts, fetchPopularPosts, fetchPrivateNextPosts, fetchNextPosts, fetchTopics, fetchPinnedPosts, fetchPrivatePinnedPosts } from 'src/modules/community/singlePost/actions/fetchActions';
import { fetchCurrentCommunity } from 'src/modules/shared/context/actions';
import { fetchCommunityAbout } from 'src/modules/community/communityAbout/actions';

import { getExpectedPostUrl } from 'src/utils/router';

import WithSession from 'src/components/hoc/withSession';
import WithErrorHandling, { RequestError } from 'src/components/hoc/withErrorHandling';

import SinglePostPage, { singlePostPageProps } from 'src/pages/community/singlePost/singlePostPage';

import { PageContext } from 'src/types';

const Page = (props: singlePostPageProps) => <SinglePostPage {...props} />;

Page.getInitialProps = async ({ res, store: { dispatch, getState }, query: { id, context: communitySlug, responses, url }, currentUser }: PageContext) => {
  const isCommunityMember = currentUser?.followedCommunities.some(({slug}) => slug === communitySlug);

  // Fetch Post and Community
  if (isCommunityMember) {
    await Promise.all([
      dispatch(fetchPrivatePost(id, communitySlug)),
      dispatch(fetchCurrentCommunity(communitySlug)),
    ]);
  } else {
    await Promise.all([
      dispatch(fetchPost(id, communitySlug)),
      dispatch(fetchCurrentCommunity(communitySlug)),
    ]);
  }

  const state = getState();
  const { currentCommunity, communityErrorStatusCode } = state.context;
  const { post, postError } = state.pages.singlePost;

  // Handle Error
  if (postError || !post || !currentCommunity) {
    if (res && postError) res.statusCode = postError.status;
    const isHiddenPrivateCommunity = communityErrorStatusCode === 310;
    if (isHiddenPrivateCommunity) {
      if (!currentUser) {
        throw new RequestError('This page is not available to visitors', 401);
      } else {
        throw new RequestError('This is a private community', 404); // user is not a member
      }
    }

    if (postError) {
      throw new RequestError('Post error', postError.status, currentCommunity);
    }

    return {};
  }

  // Fetch remaining requirements for page
  if (currentUser) {
    await Promise.all([
      dispatch(fetchCommunityAbout(communitySlug)),
      dispatch(fetchTopics(communitySlug)),
    ]);
    if (isCommunityMember) {
      await Promise.all([
        dispatch(fetchPrivatePinnedPosts(communitySlug)),
        dispatch(fetchPrivateNextPosts(id, post.dateCreated, communitySlug)),
        dispatch(fetchPrivateRelatedPosts(id, communitySlug)),
      ]);
    } else {
      await Promise.all([
        // solaris
        dispatch(fetchPinnedPosts(communitySlug)),
        dispatch(fetchRelatedPosts(id, communitySlug)),
        dispatch(fetchNextPosts(id, post.dateCreated, communitySlug)),
      ]);
    }
  } else {
    await Promise.all([
      dispatch(fetchCommunityAbout(communitySlug)),
      dispatch(fetchPopularPosts(id, communitySlug)),
      dispatch(fetchRelatedPosts(id, communitySlug)),
    ]);
  }

  // Only on initial page load on server, doesn't run on client-side transitions
  if (res && !!post && !!currentCommunity) {

    const expectedUrl = getExpectedPostUrl(post, currentCommunity.slug, decodeURI(url as string));
    const currentUrl = decodeURI(url as string);

    if (expectedUrl !== currentUrl) {
      res.writeHead(301, { Location: encodeURI(expectedUrl) });
      res.end();
      return {};
    }

    if (!currentUser) {
      // Set cache time. 5 minutes for new posts and 1 week for older ones
      const postAgeInMonths = differenceInMonths(new Date(), parseISO(post.dateLastActivity));
      const oneWeek = 60 * 60 * 24 * 7;
      const fiveMinutes = 60 * 5;
      const cacheTimeSeconds = postAgeInMonths >= 6 ? oneWeek : fiveMinutes;
      res.setHeader('Cache-Control', `max-age=${cacheTimeSeconds}, stale-while-revalidate=${cacheTimeSeconds}, no-store`);
    }
  }

  return {
    scrollToResponseId: responses,
  };
};

export default WithErrorHandling(WithSession(Page));
